import { useEffect, useMemo, useState } from "react";

import { getCampaignContacts } from "../services/campaignApi";

const RECIPIENT_MODES = [
  {
    value: "all",
    label: "Todos",
    description: "Todos los pacientes y familias con correo electrónico.",
    icon: "fa-users",
  },
  {
    value: "psychology",
    label: "Psicología",
    description: "Pacientes vinculados al área de psicología.",
    icon: "fa-brain",
  },
  {
    value: "logopedia",
    label: "Logopedia",
    description: "Pacientes vinculados al área de logopedia.",
    icon: "fa-comments",
  },
  {
    value: "nutrition",
    label: "Nutrición",
    description: "Pacientes vinculados al área de nutrición.",
    icon: "fa-apple-whole",
  },
  {
    value: "manual",
    label: "Seleccionar",
    description: "Elige individualmente los destinatarios.",
    icon: "fa-user-check",
  },
];

function getContactId(contact) {
  return (
    contact?.id ??
    contact?.patient_id ??
    contact?.patientId ??
    contact?.contact_id ??
    contact?.contactId ??
    null
  );
}

function getContactName(contact) {
  return (
    contact?.name ??
    contact?.nombre ??
    contact?.patient_name ??
    contact?.patientName ??
    contact?.full_name ??
    contact?.fullName ??
    "Sin nombre"
  );
}

function getContactEmail(contact) {
  return (
    contact?.email ??
    contact?.correo ??
    contact?.mail ??
    contact?.tutor_email ??
    contact?.tutorEmail ??
    ""
  );
}

function getContactPhone(contact) {
  return (
    contact?.phone ??
    contact?.telefono ??
    contact?.mobile ??
    contact?.movil ??
    contact?.tutor_phone ??
    contact?.tutorPhone ??
    ""
  );
}

function getContactSpecialty(contact) {
  return (
    contact?.specialty ??
    contact?.specialty_name ??
    contact?.specialtyName ??
    contact?.area ??
    contact?.especialidad ??
    ""
  );
}

function normalizeContactsResponse(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.contacts)) {
    return response.contacts;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (Array.isArray(response?.patients)) {
    return response.patients;
  }

  return [];
}

function normalizeContact(contact) {
  return {
    ...contact,
    id: getContactId(contact),
    name: getContactName(contact),
    email: getContactEmail(contact),
    phone: getContactPhone(contact),
    specialty: getContactSpecialty(contact),
  };
}

function getSpecialtyCodes(recipientMode) {
  switch (recipientMode) {
    case "psychology":
      return ["psicologia"];

    case "logopedia":
      return ["logopedia"];

    case "nutrition":
      return ["nutricion"];

    default:
      return [];
  }
}

export default function DestinatariosCard({
  campaign,
  setCampaign,
}) {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState("");

  const recipientMode = campaign?.recipientMode || "all";

  const selectedRecipients = Array.isArray(campaign?.recipients)
    ? campaign.recipients
    : [];

useEffect(() => {
  if (recipientMode !== "manual") {
    setContacts([]);
    return undefined;
  }

  const normalizedSearch = search.trim();

  if (normalizedSearch.length < 2) {
    setContacts([]);
    setContactsError("");
    setLoadingContacts(false);
    return undefined;
  }

  const controller = new AbortController();

  const timeoutId = window.setTimeout(async () => {
    try {
      setLoadingContacts(true);
      setContactsError("");

      const response = await getCampaignContacts({
        q: normalizedSearch,
        onlyWithEmail: true,
        signal: controller.signal,
      });

      const normalizedContacts = normalizeContactsResponse(response)
        .map(normalizeContact)
        .filter((contact) => contact.id !== null)
        .filter((contact) => Boolean(contact.email));

      setContacts(normalizedContacts);
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }

      console.error("Error buscando destinatarios:", error);

      setContactsError(
        error?.message ||
          "No se pudieron buscar los destinatarios.",
      );
    } finally {
      if (!controller.signal.aborted) {
        setLoadingContacts(false);
      }
    }
  }, 350);

  return () => {
    window.clearTimeout(timeoutId);
    controller.abort();
  };
}, [recipientMode, search]);

  const selectedIds = useMemo(() => {
    return new Set(
      selectedRecipients
        .map((recipient) => getContactId(recipient))
        .filter((id) => id !== null)
        .map(String),
    );
  }, [selectedRecipients]);



  function updateCampaign(updater) {
    setCampaign((currentCampaign) => {
      const safeCampaign = currentCampaign || {};

      return typeof updater === "function"
        ? updater(safeCampaign)
        : {
            ...safeCampaign,
            ...updater,
          };
    });
  }

  function handleModeChange(mode) {
    updateCampaign((currentCampaign) => ({
      ...currentCampaign,
      recipientMode: mode,
      recipients:
        mode === "manual"
          ? Array.isArray(currentCampaign.recipients)
            ? currentCampaign.recipients
            : []
          : [],
    }));

    setContactsError("");

    if (mode !== "manual") {
      setSearch("");
    }
  }

function handleAddRecipient(contact) {
  const contactId = String(contact.id);

  updateCampaign((currentCampaign) => {
    const currentRecipients = Array.isArray(
      currentCampaign.recipients,
    )
      ? currentCampaign.recipients
      : [];

    const alreadySelected = currentRecipients.some(
      (recipient) =>
        String(getContactId(recipient)) === contactId,
    );

    if (alreadySelected) {
      return currentCampaign;
    }

    return {
      ...currentCampaign,
      recipients: [
        ...currentRecipients,
        {
          id: contact.id,
          patient_id: contact.patient_id ?? contact.id,
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          specialty: contact.specialty,
        },
      ],
    };
  });
}

function handleRemoveRecipient(recipientId) {
  updateCampaign((currentCampaign) => {
    const currentRecipients = Array.isArray(
      currentCampaign.recipients,
    )
      ? currentCampaign.recipients
      : [];

    return {
      ...currentCampaign,
      recipients: currentRecipients.filter(
        (recipient) =>
          String(getContactId(recipient)) !==
          String(recipientId),
      ),
    };
  });
}


  const selectedMode = RECIPIENT_MODES.find(
    (mode) => mode.value === recipientMode,
  );

  const automaticSpecialtyCodes =
    getSpecialtyCodes(recipientMode);

  return (
    <section className="campaign-card recipients-card">
      <div className="campaign-card-header">
        <div className="campaign-card-title">
          <span className="campaign-card-icon">
            <i className="fa-solid fa-users" />
          </span>

          <div>
            <h2>Destinatarios</h2>
            <p>
              Elige a qué pacientes o familias se enviará
              la campaña.
            </p>
          </div>
        </div>


      </div>

      {recipientMode === "manual" && (
  <span className="campaign-count-badge">
    {selectedRecipients.length} seleccionados
  </span>
)}

      <div className="recipient-mode-selector">
        <span className="recipient-mode-label">Enviar a:</span>

        {RECIPIENT_MODES.map((mode) => {
          const isActive = recipientMode === mode.value;

          return (
            <button
              key={mode.value}
              type="button"
              className={`recipient-mode-button ${
                isActive ? "active" : ""
              }`}
              aria-pressed={isActive}
              onClick={() => handleModeChange(mode.value)}
            >
              <i
                className={`fa-solid ${mode.icon}`}
                aria-hidden="true"
              />
              <span>{mode.label}</span>
            </button>
          );
        })}
      </div>

      {recipientMode !== "manual" && (
        <div className="recipient-selection-summary compact">
          <i className="fa-solid fa-circle-info" />

          <div>
            <strong>
              {selectedMode?.label ||
                "Selección automática"}
            </strong>

            <p>
              {recipientMode === "all"
                ? "El servidor seleccionará automáticamente todos los contactos con correo electrónico."
                : `El servidor seleccionará automáticamente los contactos del área indicada${
                    automaticSpecialtyCodes.length
                      ? ` (${automaticSpecialtyCodes.join(
                          ", ",
                        )})`
                      : ""
                  }.`}
            </p>
          </div>
        </div>
      )}
{recipientMode === "manual" && (
  <div className="manual-recipient-section">
    <label className="recipient-search">
      <i className="fa-solid fa-magnifying-glass" />

      <input
        type="search"
        value={search}
        onChange={(event) =>
          setSearch(event.target.value)
        }
        placeholder="Buscar por nombre, correo o teléfono..."
        autoComplete="off"
      />

      {search && (
        <button
          type="button"
          className="recipient-search-clear"
          aria-label="Borrar búsqueda"
          onClick={() => setSearch("")}
        >
          <i className="fa-solid fa-xmark" />
        </button>
      )}
    </label>

    {search.trim().length < 2 && (
      <div className="recipient-search-help">
        Escribe al menos dos caracteres para buscar.
      </div>
    )}

    {loadingContacts && (
      <div className="recipient-state-message">
        <i className="fa-solid fa-spinner fa-spin" />
        Buscando destinatarios...
      </div>
    )}

    {!loadingContacts && contactsError && (
      <div className="recipient-state-message error">
        <i className="fa-solid fa-circle-exclamation" />
        {contactsError}
      </div>
    )}

    {!loadingContacts &&
      !contactsError &&
      search.trim().length >= 2 &&
      contacts.length === 0 && (
        <div className="recipient-state-message">
          <i className="fa-solid fa-magnifying-glass" />
          No se encontraron destinatarios.
        </div>
      )}

    {!loadingContacts &&
      !contactsError &&
      contacts.length > 0 && (
        <div className="recipient-search-results">
          {contacts.map((contact) => {
            const isSelected = selectedIds.has(
              String(contact.id),
            );

            return (
              <div
                key={contact.id}
                className="recipient-search-result"
              >
                <span className="recipient-avatar">
                  {contact.name
                    .trim()
                    .charAt(0)
                    .toUpperCase() || "?"}
                </span>

                <span className="recipient-information">
                  <strong>{contact.name}</strong>

                  <span className="recipient-email">
                    <i className="fa-solid fa-envelope" />
                    {contact.email}
                  </span>

                  {(contact.phone ||
                    contact.specialty) && (
                    <small>
                      {contact.specialty && (
                        <span>
                          <i className="fa-solid fa-tag" />
                          {contact.specialty}
                        </span>
                      )}

                      {contact.phone && (
                        <span>
                          <i className="fa-solid fa-phone" />
                          {contact.phone}
                        </span>
                      )}
                    </small>
                  )}
                </span>

                <button
                  type="button"
                  className="recipient-add-button"
                  disabled={isSelected}
                  onClick={() =>
                    handleAddRecipient(contact)
                  }
                >
                  <i
                    className={`fa-solid ${
                      isSelected
                        ? "fa-check"
                        : "fa-plus"
                    }`}
                  />

                  {isSelected ? "Añadido" : "Añadir"}
                </button>
              </div>
            );
          })}
        </div>
      )}

    <div className="selected-recipients-section">
      <div className="selected-recipients-heading">
        <div>
          <strong>Seleccionados</strong>
          <span>
            {selectedRecipients.length} destinatarios
          </span>
        </div>
      </div>

      {selectedRecipients.length === 0 ? (
        <div className="selected-recipients-empty">
          Todavía no has añadido ningún destinatario.
        </div>
      ) : (
        <div className="selected-recipients-list">
          {selectedRecipients.map((recipient) => {
            const recipientId = getContactId(recipient);

            return (
              <div
                key={recipientId}
                className="selected-recipient-chip"
              >
                <span>
                  <strong>
                    {getContactName(recipient)}
                  </strong>
                  <small>
                    {getContactEmail(recipient)}
                  </small>
                </span>

                <button
                  type="button"
                  aria-label={`Quitar a ${getContactName(
                    recipient,
                  )}`}
                  onClick={() =>
                    handleRemoveRecipient(recipientId)
                  }
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
)}
    </section>
  );
}